import React, { useState } from 'react';
import { FaFileWord, FaDownload, FaExternalLinkAlt, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import './TemplatesPage.css';

const databases = [
  'EI Compendex',
  'SCOPUS',
  'SCImago',
  'INSPEC',
  'Norwegian Register for Scientific Journals and Series',
  'zbMATH',
];

const templateFiles = [
  {
    name: 'Full Paper Template',
    file: '/templates/ICES2026_Full_Paper_template.docx',
    format: '.docx',
    note: 'Maximum length for Full Papers = 8 pages',
  },
  {
    name: 'Extended Abstract Template',
    file: '/templates/ICES2026_Extended_Abstract_template.docx',
    format: '.docx',
    note: 'Maximum length for Extended Abstracts = 4 pages',
  },
  {
    name: 'Contributors\' Agreement Form',
    file: '/templates/ICES2026_Contributors_Agreement_template.docx',
    format: '.docx',
  },
  {
    name: 'Permission Request Form',
    file: '/templates/ICES2026_Permission_Request_Form.docx',
    format: '.docx',
  },
  {
    name: 'Single-page Abstract Template',
    file: '/templates/ICES2026_Single_Page_Abstract_template.docx',
    format: '.docx',
  },
];

const TemplatesPage = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <section className="templates-page fade-in-up">
      <div className="templates-wrapper">
        <span className="badge templates-badge">Submission</span>
        <h1 className="templates-heading">Full Paper (or Abstract) Submission</h1>
        <p className="templates-subtitle">
          The conference proceedings, comprising papers presented at the conference, will be published, subject to authors' consent, in <strong>Lecture Notes in Civil Engineering</strong> by Springer Nature and indexed in the following major databases:
        </p>

        {/* Database chips */}
        <div className="templates-databases">
          {databases.map((db) => (
            <span className="db-chip" key={db}>
              <FaCheckCircle className="chip-icon" />
              {db}
            </span>
          ))}
        </div>

        {/* Submission intro */}
        <p className="templates-subtitle" style={{ marginBottom: '32px' }}>
          Four options are available for submission. Please select one of the following options during submission through the <a href="https://ices2026-portal.com" target="_blank" rel="noopener noreferrer" id="system-button">online portal</a>.
        </p>

        {/* Option Cards */}
        <div className="templates-options">
          {/* OPTION 1 */}
          <div
            className={`option-card${expandedCard === 0 ? ' expanded' : ''}`}
            onClick={() => toggleCard(0)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleCard(0)}
          >
            <div className="option-card-header">
              <div className="option-number">1</div>
              <div className="option-card-header-text">
                <div className="option-card-label">Option 1</div>
                <div className="option-card-title">Conference Use Only (Not Published in the Proceedings)</div>
              </div>
            </div>
            <div className="option-card-body">
              <p className="option-card-description">
                The submitted paper will be included in the conference USB drive, provided as part of the conference package, and made available to all registered delegates. It will <strong>not</strong> be published in the conference proceedings.
              </p>
              <div className="option-card-docs-heading">Documents to be included in your ZIP file submission:</div>
              <ul className="option-card-docs-list">
                <li>
                  Your full paper in both <strong>.docx</strong> and <strong>.pdf</strong> formats, prepared using the paper template provided{' '}
                  <a href="/templates/ICES2026_Full_Paper_template.docx" download>here</a>.
                </li>
              </ul>
            </div>
          </div>

          {/* OPTION 2 */}
          <div
            className={`option-card${expandedCard === 1 ? ' expanded' : ''}`}
            onClick={() => toggleCard(1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleCard(1)}
          >
            <div className="option-card-header">
              <div className="option-number">2</div>
              <div className="option-card-header-text">
                <div className="option-card-label">Option 2</div>
                <div className="option-card-title">Conference Use and Official Publication in the Proceedings</div>
              </div>
            </div>
            <div className="option-card-body">
              <p className="option-card-description">
                The submitted paper will be included in the conference program and officially published in the conference proceedings, <strong>Lecture Notes in Civil Engineering</strong> by Springer Nature.
              </p>
              <div className="option-card-docs-heading">Documents to be included in your ZIP file submission:</div>
              <ul className="option-card-docs-list">
                <li>
                  Your full paper in both <strong>.docx</strong> and <strong>.pdf</strong> formats, prepared using the paper template provided{' '}
                  <a href="/templates/ICES2026_Full_Paper_template.docx" download>here</a>.
                </li>
                <li>
                  Contributors' agreement form to be downloaded{' '}
                  <a href="/templates/ICES2026_Contributors_Agreement_template.docx" download>here</a>.
                </li>
                <li>
                  Permission request form (optional) – download{' '}
                  <a href="/templates/ICES2026_Permission_Request_Form.docx" download>here</a>{' '}
                  – if any author(s) use materials that were published by their own or by others.
                </li>
              </ul>
              {/* <p className="option-card-description" style={{ marginTop: '12px', marginBottom: 0 }}>
                If authors do not wish to submit a full paper, an option to submit an extended abstract is available.
              </p> */}
            </div>
          </div>

          {/* OPTION 3 */}
          <div
            className={`option-card${expandedCard === 2 ? ' expanded' : ''}`}
            onClick={() => toggleCard(2)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleCard(2)}
          >
            <div className="option-card-header">
              <div className="option-number">3</div>
              <div className="option-card-header-text">
                <div className="option-card-label">Option 3</div>
                <div className="option-card-title">Extended Abstract Submission</div>
              </div>
            </div>
            <div className="option-card-body">
              <p className="option-card-description">
                The extended abstract will be included in the conference program only. It will <strong>not</strong> be published in the conference proceedings.
              </p>
              <div className="option-card-docs-heading">Documents to be included in your ZIP file submission:</div>
              <ul className="option-card-docs-list">
                <li>
                  Your extended abstract in both <strong>.docx</strong> and <strong>.pdf</strong> formats, prepared using the extended abstract template provided{' '}
                  <a href="/templates/ICES2026_Extended_Abstract_template.docx" download>here</a>.
                </li>
              </ul>
            </div>
          </div>

          {/* OPTION 4 */}
          <div
            className={`option-card${expandedCard === 3 ? ' expanded' : ''}`}
            onClick={() => toggleCard(3)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && toggleCard(3)}
          >
            <div className="option-card-header">
              <div className="option-number">4</div>
              <div className="option-card-header-text">
                <div className="option-card-label">Option 4</div>
                <div className="option-card-title">Single-page Abstract Submission</div>
              </div>
            </div>
            <div className="option-card-body">
              <p className="option-card-description">
                The single-page abstract will be included in the conference program only. It will <strong>not</strong> be published in the conference proceedings.
              </p>
              <div className="option-card-docs-heading">Documents to be included in your ZIP file submission:</div>
              <ul className="option-card-docs-list">
                <li>
                  Your single-page abstract in both <strong>.docx</strong> and <strong>.pdf</strong> formats, prepared using the single-page abstract template provided{' '}
                  <a href="/templates/ICES2026_Single_Page_Abstract_template.docx" download>here</a>.
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Download Templates Section */}
        <h2 className="templates-downloads-heading">Download Templates</h2>
        <p className="templates-downloads-subtitle">All templates are in Microsoft Word (.docx) format.</p>

        <div className="downloads-grid">
          {templateFiles.map((tpl) => (
            <a
              key={tpl.name}
              href={tpl.file}
              download
              className="download-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="download-icon-wrapper">
                <FaFileWord />
              </div>
              <div className="download-card-text">
                <div className="download-card-title">{tpl.name}</div>
                {tpl.note && <div className="download-card-note">{tpl.note}</div>}
                <div className="download-card-format">{tpl.format}</div>
              </div>
              <FaDownload className="download-arrow" />
            </a>
          ))}
        </div>

        {/* Proceedings footer */}
        <div className="templates-footer">
          <div className="templates-footer-icon">
            <FaInfoCircle />
          </div>
          <div className="templates-footer-text">
            For more information about the proceedings, please read the{' '}
            <a href="https://www.springer.com/gp/authors-editors/conference-proceedings" target="_blank" rel="noreferrer">
              Springer Nature Conference Proceedings website <FaExternalLinkAlt style={{ fontSize: '0.7rem', marginLeft: '4px' }} />
            </a>.
          </div>
        </div>
      </div>
    </section>
  );
};

export default TemplatesPage;
